<aside class="sidepanel">
  <header class="sidepanel__head">
    <app-icon name="check" size="18" />
    <span class="sidepanel__title">{{ S['Data validation'] }}</span>
    <button class="iconbtn iconbtn--bare" [title]="S['Close']" (click)="cancelled.emit()" [attr.aria-label]="S['Close']">
      <app-icon name="close" size="16" />
    </button>
  </header>

  <div class="sidepanel__body">
    <label class="field">
      <span class="field__label">{{ S['Apply to range'] }}</span>
      <span class="field__row">
        <input class="input" [value]="range()" (input)="setRange($event)" [attr.aria-label]="S['Apply to range']"/>
        <button class="iconbtn" [title]="S['Pick range']" [attr.aria-label]="S['Pick range']"><app-icon name="grid" size="15" /></button>
      </span>
    </label>

    <label class="field">
      <span class="field__label">{{ S['Criteria'] }}</span>
      <select class="input" (change)="setCriteria($event)" [attr.aria-label]="S['Criteria']">
        @for (c of criteriaTypes; track c.key) {
          <option [value]="c.key" [selected]="criteria() === c.key">{{ c.label }}</option>
        }
      </select>
    </label>

    @if (criteria() === 'listRange') {
      <label class="field">
        <span class="field__label">{{ S['Options come from'] }}</span>
        <span class="field__row">
          <input class="input" [value]="sourceRange()" (input)="setSourceRange($event)" [attr.aria-label]="S['Options come from']"/>
          <button class="iconbtn" [title]="S['Pick range']" [attr.aria-label]="S['Pick range']"><app-icon name="grid" size="15" /></button>
        </span>
      </label>
    }

    @if (criteria() === 'custom') {
      <label class="field">
        <span class="field__label">{{ S['Custom formula'] }}</span>
        <input class="input" [value]="formula()" (input)="setFormula($event)" [attr.aria-label]="S['Custom formula']"/>
      </label>
    }

    @if (criteria() === 'checkbox') {
      <label class="field">
        <span class="field__label">{{ S['Checked value'] }}</span>
        <input class="input" [value]="checkedValue()" [placeholder]="S['TRUE']" (input)="setCheckedValue($event)" [attr.aria-label]="S['Checked value']"/>
      </label>
      <label class="field">
        <span class="field__label">{{ S['Unchecked value'] }}</span>
        <input class="input" [value]="uncheckedValue()" [placeholder]="S['FALSE']" (input)="setUncheckedValue($event)" [attr.aria-label]="S['Unchecked value']"/>
      </label>
    }

    @if (usesOperator()) {
      <label class="field">
        <span class="field__label">{{ operatorLabel() }}</span>
        <select class="input" (change)="setOperator($event)" [attr.aria-label]="operatorLabel()">
          @for (o of operators; track o.key) {
            <!-- Dates have no requireDateNotEqualTo, so the option is disabled
                 rather than offered and silently dropped. -->
            <option
              [value]="o.key"
              [selected]="operator() === o.key"
              [disabled]="criteria() === 'date' && dateUnsupported.includes(o.key)"
            >
              {{ o.label }}
            </option>
          }
        </select>
      </label>
      <div class="field">
        <span class="field__label">{{ needsTwoValues() ? S['Between'] : S['Value'] }}</span>
        <span class="field__row">
          <input class="input" [type]="valueInputType()" [value]="value1()" (input)="setValue1($event)" [attr.aria-label]="needsTwoValues() ? S['Between'] : S['Value']" />
          @if (needsTwoValues()) {
            <input class="input" [type]="valueInputType()" [value]="value2()" (input)="setValue2($event)" [attr.aria-label]="S['Between']" />
          }
        </span>
      </div>
    }

    @if (criteria() === 'list') {
      <div class="field">
        <span class="field__label">{{ S['Options'] }}</span>
        @for (o of options(); track $index) {
          <span class="option">
            <span class="option__grip"><app-icon name="dots" size="14" /></span>
            <button
              class="option__swatch"
              [style.background]="o.colour"
              [title]="o.colour"
              [attr.aria-label]="o.colour"
              (click)="cycleColour($index)"
            ></button>
            <input class="input option__value" [value]="o.value" (input)="setOption($index, $event)" [attr.aria-label]="optionLabel($index)" [placeholder]="optionLabel($index)" />
            <button class="iconbtn iconbtn--bare" [title]="S['Remove option']" (click)="removeOption($index)" [attr.aria-label]="S['Remove option']">
              <app-icon name="close" size="14" />
            </button>
          </span>
        }
        <button class="linkbtn" (click)="addOption()" [attr.aria-label]="S['Add option']"><app-icon name="plus" size="14" /> {{ S['Add option'] }}</button>
      </div>
    }

    @if (isList()) {
      <div class="field">
        <span class="field__label">{{ S['Show as'] }}</span>
        <span class="segmented">
          @for (m of showAsModes; track m.key) {
            <button class="segmented__btn" [class.segmented__btn--on]="showAs() === m.key" (click)="showAs.set(m.key)" [attr.aria-label]="m.label">
              {{ m.label }}
            </button>
          }
        </span>
      </div>

      <label class="checkrow">
        <span>{{ S['Allow more than one choice'] }}</span>
        <input type="checkbox" [checked]="multiple()" (change)="multiple.set(!multiple())" [attr.aria-label]="S['Allow more than one choice']" />
      </label>
    }
    <label class="checkrow">
      <span>{{ S['Allow blank cells'] }}</span>
      <input type="checkbox" [checked]="allowBlank()" (change)="allowBlank.set(!allowBlank())" [attr.aria-label]="S['Allow blank cells']" />
    </label>

    <div class="field">
      <span class="field__label field__label--collapsible">
        <app-icon name="chevrondown" size="13" /> {{ S['When data is invalid'] }}
      </span>
      <select class="input" (change)="setOnInvalid($event)" [attr.aria-label]="S['When data is invalid']">
        <option value="reject" [selected]="onInvalid() === 'reject'">{{ S['Reject the entry'] }}</option>
        <option value="warn" [selected]="onInvalid() === 'warn'">{{ S['Show a warning'] }}</option>
      </select>
    </div>

    <label class="checkrow">
      <span>{{ S['Show help text on select'] }}</span>
      <input type="checkbox" [checked]="showHelp()" (change)="showHelp.set(!showHelp())" [attr.aria-label]="S['Show help text on select']" />
    </label>
    @if (showHelp()) {
      <input class="input" [value]="helpText()" (input)="setHelp($event)" [attr.aria-label]="S['Help text']" />
    }
  </div>

  <footer class="sidepanel__foot">
    @if (replaceId()) {
      <button class="linkbtn linkbtn--danger" (click)="removeRule()" [attr.aria-label]="S['Remove rule']">{{ S['Remove rule'] }}</button>
    }
    <span class="sidepanel__spacer"></span>
    <button class="btn btn--ghost" (click)="cancelled.emit()" [attr.aria-label]="S['Cancel']">{{ S['Cancel'] }}</button>
    <button class="btn btn--primary" (click)="emitDone()" [attr.aria-label]="S['Done']">{{ S['Done'] }}</button>
  </footer>
</aside>
